<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-certificate me-2"></i> {{ $t('certificateType.title') }}
      </h4>

      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('certificateType.name') }}</label>
              <input type="text" class="form-control" v-model="form.name" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('certificateType.description') }}</label>
              <input type="text" class="form-control" v-model="form.description" />
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('certificateType.update') : $t('certificateType.add') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('certificateType.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('certificateType.existingCertificateTypes') }}
      </h5>

      <div v-if="types.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('certificateType.name') }}</th>
              <th>{{ $t('certificateType.description') }}</th>
              <th class="text-center">{{ $t('certificateType.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="type in types" :key="type.id">
              <td>{{ type.name }}</td>
              <td>{{ type.description || '-' }}</td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadType(type)">{{ $t('certificateType.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm" @click="deleteType(type.id)">{{ $t('certificateType.delete') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('certificateType.noCertificateTypes') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import axios from '@/api'

const toast = useToast()
const types = ref([])
const editingId = ref(null)

const form = ref({
  name: '',
  description: ''
})

const fetchTypes = async () => {
  try {
    const res = await axios.get('/certificate-types')
    types.value = res.data.data.data || [] // ✅ fix pagination structure
  } catch (err) {
    toast.error($t('certificateType.failedToLoadCertificateTypes'))
    console.error(err)
  }
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await axios.put(`/certificate-types/${editingId.value}`, form.value)
      toast.success($t('certificateType.certificateTypeUpdated'))
    } else {
      await axios.post('/certificate-types', form.value)
      toast.success($t('certificateType.certificateTypeAdded'))
    }
    resetForm()
    fetchTypes()
  } catch (err) {
    toast.error($t('certificateType.errorSavingCertificateType'))
    console.error(err)
  }
}

const loadType = (type) => {
  editingId.value = type.id
  form.value = {
    name: type.name,
    description: type.description || ''
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    name: '',
    description: ''
  }
}

const deleteType = async (id) => {
  if (!confirm($t('certificateType.confirmDelete'))) return
  try {
    await axios.delete(`/certificate-types/${id}`)
    toast.success($t('certificateType.certificateTypeDeleted'))
    fetchTypes()
  } catch (err) {
    toast.error($t('certificateType.failedToDeleteCertificateType'))
    console.error(err)
  }
}

onMounted(fetchTypes)
</script>
